/**
 * @module components/AI/ChatAssistant
 * @description Interfaz de chat conversacional del asistente IA del sistema ACSGC.
 *
 * Renderiza el historial de mensajes (usuario/asistente), el campo de entrada
 * y un toolbar con acciones de exportación. Se comunica exclusivamente a través
 * de `AIContext.sendMessage()` — no realiza llamadas HTTP directas.
 *
 * Funcionalidades:
 * - Historial de conversación con markdown renderizado
 * - Indicador de "escribiendo..." durante la generación
 * - Exportar conversación a PDF o Markdown
 * - Limpiar historial con confirmación
 * - Modo compacto (minimizado) vs expandido
 * - Indicador de límite diario de consultas
 *
 * @see AIContext para la lógica de envío de mensajes y gestión del historial
 * @see assistantGemini.ts para el proveedor Gemini subyacente
 */
import React, { useState, useRef, useEffect } from 'react';
import {
  MessageCircle,
  Send,
  Trash2,
  Download,
  Settings,
  Loader,
  AlertCircle,
  Sparkles,
  X,
  Minimize2,
  Maximize2,
  Activity
} from 'lucide-react';
import { useAI } from '../../contexts/AIContext';
import { getUsageStats, getRemainingQueries } from '../../lib/ai/geminiClient';
import type { ISOControl } from '../../types/iso';

interface ChatAssistantProps {
  control?: ISOControl;
  onClose?: () => void;
  className?: string;
}

export function ChatAssistant({ control, onClose, className = '' }: ChatAssistantProps) {
  const {
    conversationHistory,
    sendMessage,
    isLoading,
    error,
    isEnabled,
    apiKeyConfigured,
    clearHistory,
    exportConversation
  } = useAI();

  const [input, setInput] = useState('');
  const [isMinimized, setIsMinimized] = useState(false);
  const [usageCount, setUsageCount] = useState(0);
  const [remainingQueries, setRemainingQueries] = useState(50);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Actualizar contador de uso
  const updateUsageStats = () => {
    const stats = getUsageStats();
    const remaining = getRemainingQueries();
    setUsageCount(stats.count);
    setRemainingQueries(remaining);
  };

  // Auto-scroll al final cuando hay nuevos mensajes
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [conversationHistory]);

  // Focus en input cuando se abre
  useEffect(() => {
    if (!isMinimized) {
      inputRef.current?.focus();
    }
  }, [isMinimized]);

  // Actualizar estadísticas de uso al montar y cuando cambia el historial
  useEffect(() => {
    updateUsageStats();
  }, [conversationHistory]);

  // =====================================================
  // HANDLERS
  // =====================================================

  const handleSend = async () => {
    if (!input.trim() || isLoading) return;

    const message = input.trim();
    setInput('');

    try {
      await sendMessage(message, control?.id);
    } catch (err) {
      console.error('[CHAT] Error enviando mensaje:', err);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleExport = () => {
    const text = exportConversation();
    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `conversacion-ia-${new Date().toISOString().split('T')[0]}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // =====================================================
  // SUGERENCIAS RÁPIDAS
  // =====================================================

  const quickSuggestions = control
    ? [
        `¿Qué evidencias necesito para el control ${control.code}?`,
        `¿Cómo implemento este control paso a paso?`,
        `¿Qué riesgos existen si no cumplo este control?`,
        'Dame un checklist de tareas para este control'
      ]
    : [
        '¿Qué es ISO 27001 y cómo empiezo?',
        '¿Cuáles son los controles más críticos?',
        '¿Cómo preparo una auditoría de certificación?',
        'Explica el proceso de gestión de riesgos'
      ];

  // =====================================================
  // RENDER: ESTADO NO CONFIGURADO
  // =====================================================

  if (!apiKeyConfigured) {
    return (
      <div className={`bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-xl p-6 ${className}`}>
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <h3 className="text-sm font-semibold text-yellow-900 dark:text-yellow-100 mb-1">
              Asistente de IA no configurado
            </h3>
            <p className="text-sm text-yellow-800 dark:text-yellow-200 mb-3">
              Para usar el asistente de cumplimiento, necesitas configurar tu API key de OpenAI.
            </p>
            <button
              onClick={() => {/* Abrir settings */}}
              className="text-sm text-yellow-900 dark:text-yellow-100 underline hover:no-underline"
            >
              Ir a Configuración
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!isEnabled) {
    return (
      <div className={`bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-6 ${className}`}>
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <h3 className="text-sm font-semibold text-red-900 dark:text-red-100 mb-1">
              Error de configuración
            </h3>
            <p className="text-sm text-red-800 dark:text-red-200">
              Hubo un problema al inicializar el asistente de IA. Por favor, verifica tu API key.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // =====================================================
  // RENDER: MINIMIZADO
  // =====================================================

  if (isMinimized) {
    return (
      <div className={`bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-4 ${className}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            <span className="text-sm font-medium text-gray-900 dark:text-white">
              Asistente de IA {control ? `- ${control.code}` : ''}
            </span>
          </div>
          <button
            onClick={() => setIsMinimized(false)}
            className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
            title="Expandir"
          >
            <Maximize2 className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  }

  // =====================================================
  // RENDER: COMPLETO
  // =====================================================

  return (
    <div className={`bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl flex flex-col ${className}`}>
      {/* HEADER */}
      <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="p-2 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg">
            <Sparkles className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
              Asistente de Cumplimiento ISO
            </h3>
            {control && (
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Contexto: {control.code} - {control.title}
              </p>
            )}
            {/* Contador de consultas */}
            <div className="flex items-center gap-1.5 mt-1">
              <Activity className={`w-3 h-3 ${remainingQueries < 10 ? 'text-red-500' : remainingQueries < 25 ? 'text-yellow-500' : 'text-green-500'}`} />
              <span className={`text-xs font-medium ${remainingQueries < 10 ? 'text-red-600 dark:text-red-400' : remainingQueries < 25 ? 'text-yellow-600 dark:text-yellow-400' : 'text-green-600 dark:text-green-400'}`}>
                {remainingQueries} consultas restantes hoy
              </span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1">
          {conversationHistory.length > 0 && (
            <>
              <button
                onClick={handleExport}
                className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
                title="Exportar conversación"
              >
                <Download className="w-4 h-4" />
              </button>
              <button
                onClick={clearHistory}
                className="p-2 text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                title="Limpiar historial"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </>
          )}
          <button
            onClick={() => setIsMinimized(true)}
            className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
            title="Minimizar"
          >
            <Minimize2 className="w-4 h-4" />
          </button>
          {onClose && (
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
              title="Cerrar"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* MENSAJES */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4" style={{ maxHeight: '500px', minHeight: '300px' }}>
        {conversationHistory.length === 0 ? (
          <div className="text-center py-8">
            <div className="inline-flex p-4 bg-blue-100 dark:bg-blue-900/20 rounded-full mb-4">
              <MessageCircle className="w-8 h-8 text-blue-600 dark:text-blue-400" />
            </div>
            <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              ¡Hola! Soy tu asistente de cumplimiento
            </h4>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4 max-w-md mx-auto">
              Puedo ayudarte con preguntas sobre ISO 27001/27002, sugerir evidencias,
              crear tareas y analizar el estado de tus controles.
            </p>

            {/* Sugerencias rápidas */}
            <div className="mt-6">
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                Prueba con estas preguntas:
              </p>
              <div className="space-y-2">
                {quickSuggestions.slice(0, 3).map((suggestion, idx) => (
                  <button
                    key={idx}
                    onClick={() => setInput(suggestion)}
                    className="block w-full text-left px-4 py-2 bg-gray-50 dark:bg-gray-700/50 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg text-sm text-gray-700 dark:text-gray-300 transition-colors"
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <>
            {conversationHistory.map((msg) => (
              <div
                key={msg.id}
                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[80%] rounded-lg px-4 py-2 ${
                    msg.role === 'user'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white'
                  }`}
                >
                  <div className="text-sm whitespace-pre-wrap">{msg.content}</div>
                  <div className={`text-xs mt-1 ${
                    msg.role === 'user' ? 'text-blue-100' : 'text-gray-500 dark:text-gray-400'
                  }`}>
                    {new Date(msg.timestamp).toLocaleTimeString('es-ES', {
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </div>
                </div>
              </div>
            ))}

            {isLoading && (
              <div className="flex justify-start">
                <div className="bg-gray-100 dark:bg-gray-700 rounded-lg px-4 py-3">
                  <div className="flex items-center gap-2">
                    <Loader className="w-4 h-4 text-gray-600 dark:text-gray-400 animate-spin" />
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      Pensando...
                    </span>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* ERROR */}
      {error && (
        <div className="px-4 py-2 bg-red-50 dark:bg-red-900/20 border-t border-red-200 dark:border-red-800">
          <div className="flex items-start gap-2">
            <AlertCircle className="w-4 h-4 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-red-800 dark:text-red-200">{error}</p>
          </div>
        </div>
      )}

      {/* INPUT */}
      <div className="p-4 border-t border-gray-200 dark:border-gray-700">
        <div className="flex gap-2">
          <input
            ref={inputRef}
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder={control ? `Pregunta sobre ${control.code}...` : 'Pregunta sobre ISO 27001/27002...'}
            className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            disabled={isLoading}
          />
          <button
            onClick={handleSend}
            disabled={!input.trim() || isLoading}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            <Send className="w-4 h-4" />
            <span className="hidden sm:inline">Enviar</span>
          </button>
        </div>

        {/* Sugerencias rápidas al principio */}
        {conversationHistory.length === 0 && (
          <div className="mt-3 flex flex-wrap gap-2">
            {quickSuggestions.slice(0, 2).map((suggestion, idx) => (
              <button
                key={idx}
                onClick={() => setInput(suggestion)}
                className="text-xs px-3 py-1.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-full hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
              >
                {suggestion.length > 40 ? suggestion.substring(0, 40) + '...' : suggestion}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
